import clsx from 'clsx'
import { useState } from 'react'
import { AddCollaborator, ArrowDown, ArrowUp, DashedAvatarRing, OpenNew } from '@/assets/icons'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { OrgLogo } from './OrgLogo'
import { EditableOrgLogo } from './EditableOrgLogo'
import { OrgBillingStats } from './OrgBillingStats'
import { SearchField } from '@/components/search-field/SearchField'
import { Tag } from '@/components/tag/Tag'
import { Button } from '@/components/button/Button'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import { getInitialsFromString } from '@/util/string-utils'
import InviteListItemMenu from './InviteListItemMenu'
import UserListItemMenu from './UserListItemMenu'
import { UserRole } from '@/organizations/schemas'
import { InlineMessage } from '@/components/inline-message/InlineMessage'

export interface MemberRosterMember {
  id: string
  displayName: string | null
  email: string
  role: UserRole | null
  isPending: boolean
  userId?: string // DB ID for mutations (undefined for pending rows)
  isOrgPayer?: boolean // suppresses demote + remove for active members
}

export interface BillingStats {
  paidSeats: number
  pendingInviteCount: number
  billingCadence: string
  seatCost: string
}

export interface MembersRosterProps {
  orgName: string
  orgLogoUrl?: string
  currentUserId: string
  members: MemberRosterMember[]
  billingStats?: BillingStats
  onManageSubscription?: () => void
  onInviteMembers?: () => void
  /** When provided (org admins only), the logo tile becomes a picker that uploads/replaces the org logo. */
  onLogoFileSelected?: (file: File) => void
  /** When provided (org admins only) and a logo exists, a delete affordance appears on the logo tile. */
  onLogoDelete?: () => void
  /** Shows a spinner on the logo tile while a logo upload or delete is in flight. */
  isLogoBusy?: boolean
  /** When true (org admins only), show the action menu on active member rows. */
  showMemberMenu?: boolean
  /** Refreshes the roster after a member action (role change, remove). */
  onMemberAction: () => void
  /** When true (org admins only), show the invite action menu on pending invite rows. */
  showInviteMenu?: boolean
  /** Refreshes the roster after an invite Resend or Revoke. */
  onInviteAction: () => void
  /** When true, replaces the member list with an error message. */
  isError?: boolean
  /** When true, shows skeleton rows in place of the member list while data is fetching. */
  isLoading?: boolean
}

/**
 * Presentational roster body: org header (logo + name + optional Manage Subscription), the billing
 * stats grid (admins), and the searchable/sortable members table. Takes primitives and callbacks
 * only — no data fetching, no modal chrome — so it can be rendered inside the standalone
 * MemberRosterModal (sidebar) and inside the settings-revamp Members panels identically.
 */
export function MembersRoster({
  orgName,
  orgLogoUrl,
  currentUserId,
  members,
  billingStats,
  onManageSubscription,
  onInviteMembers,
  onLogoFileSelected,
  onLogoDelete,
  isLogoBusy,
  showMemberMenu,
  onMemberAction,
  showInviteMenu,
  onInviteAction,
  isError,
  isLoading,
}: MembersRosterProps) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<'name' | 'role'>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const hasActionsColumn = Boolean(showMemberMenu) || Boolean(showInviteMenu)

  const toggleSort = (key: 'name' | 'role') => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const searchLower = search.toLowerCase()
  const filteredMembers = search
    ? members.filter((member) => (member.displayName?.toLowerCase().includes(searchLower) ?? false) || member.email.toLowerCase().includes(searchLower))
    : members

  const sortedMembers = [...filteredMembers].sort((a, b) => {
    if (sortKey === 'name') {
      const aKey = (a.displayName ?? a.email).toLowerCase()
      const bKey = (b.displayName ?? b.email).toLowerCase()
      return sortDir === 'asc' ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey)
    }
    const weight = (m: MemberRosterMember) => (m.isPending ? 2 : m.role === UserRole.ADMIN ? 0 : 1)
    return sortDir === 'asc' ? weight(a) - weight(b) : weight(b) - weight(a)
  })

  return (
    // On mobile, `h-auto` lets the whole panel grow past the viewport so the scroll container scrolls
    // as one unit (org header, billing, and rows together). Desktop keeps `h-full` with only rows scrolling.
    <div className="flex h-full max-sm:h-auto min-h-0 min-w-0 max-w-content-area mx-auto flex-col">
      {/* Title + org icon/logo */}
      <div className="shrink-0 pt-2 px-6 pb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {onLogoFileSelected ? (
            <EditableOrgLogo orgName={orgName} orgLogoUrl={orgLogoUrl} onFileSelected={onLogoFileSelected} onDelete={onLogoDelete} isBusy={isLogoBusy} />
          ) : (
            <OrgLogo orgName={orgName} orgLogoUrl={orgLogoUrl} />
          )}
          {/* Styled like a heading but not a heading role: the dialog/panel that renders this roster
              owns the accessible title (the standalone modal supplies a hidden Dialog.Title). */}
          <p className="min-w-0 flex-1 font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950">{orgName}</p>
        </div>

        {/* Manage Subscription */}
        {onManageSubscription && (
          <Button
            onClick={onManageSubscription}
            color="primary"
            variant="plain"
            size="sm"
            trailingIcon={<OpenNew className="size-4" aria-hidden />}
            className="sm:ml-auto"
          >
            Manage Subscription
          </Button>
        )}
      </div>

      {/* Body: billing stats + members table. On desktop only rows scroll; on mobile the whole panel
          flows naturally (`max-sm:flex-none`) and scrolls as one unit. */}
      <div className="flex min-h-0 flex-1 max-sm:flex-none flex-col gap-6 px-6 pb-6">
        {isError && <InlineMessage variant="error">Could not load members. Refresh the page and try again.</InlineMessage>}

        {/* Billing stats */}
        {!isError && billingStats && <OrgBillingStats billingStats={billingStats} />}

        {/* Members table */}
        {!isError && (
          <div className="flex min-h-0 flex-col overflow-clip rounded-md border border-brand-neutral-200 bg-white">
            {/* Toolbar */}
            <div className="shrink-0 flex flex-col gap-2 border-b border-brand-neutral-200 bg-brand-neutral-50 px-2 py-1.5 sm:flex-row sm:items-center sm:gap-3">
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Search name or email"
                aria-label="Search members by name or email"
                className="flex-1 border-none !bg-transparent"
              />
              {onInviteMembers && (
                <Button
                  onClick={onInviteMembers}
                  color="primary"
                  variant="filled"
                  size="sm"
                  leadingIcon={<AddCollaborator className="size-4" aria-hidden />}
                  className="w-full sm:w-auto"
                >
                  Invite Members
                </Button>
              )}
            </div>

            {/* Table - only rows scroll on desktop; on mobile rows expand and the outer panel scrolls. */}
            <div className="min-h-0 flex-1 overflow-y-auto max-sm:flex-none max-sm:overflow-visible">
              <table className="w-full table-fixed">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      aria-sort={sortKey === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                      className="sticky max-sm:static top-0 z-10 border-b border-brand-neutral-200 bg-brand-neutral-50 px-5 py-2.5 text-left text-sm font-medium text-brand-neutral-950"
                    >
                      <button type="button" onClick={() => toggleSort('name')} className="flex items-center gap-1 hover:text-brand-neutral-700">
                        Members
                        <SortIcon active={sortKey === 'name'} dir={sortDir} />
                      </button>
                    </th>
                    <th
                      scope="col"
                      aria-sort={sortKey === 'role' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                      className="sticky max-sm:static top-0 z-10 w-24 border-b border-brand-neutral-200 bg-brand-neutral-50 px-5 py-2.5 text-left text-sm font-medium text-brand-neutral-950"
                    >
                      <button type="button" onClick={() => toggleSort('role')} className="flex items-center gap-1 hover:text-brand-neutral-700">
                        Role
                        <SortIcon active={sortKey === 'role'} dir={sortDir} />
                      </button>
                    </th>
                    {hasActionsColumn && (
                      <th scope="col" className="sticky max-sm:static top-0 z-10 w-10 border-b border-brand-neutral-200 bg-brand-neutral-50 px-3 py-2.5">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <MemberRowsSkeleton hasActionsColumn={hasActionsColumn} />
                  ) : (
                    <>
                      {filteredMembers.length === 0 && (
                        <tr>
                          <td colSpan={hasActionsColumn ? 3 : 2} className="px-5 py-8 text-center text-sm text-brand-neutral-600">
                            {search ? 'No members match your search.' : 'No members to display.'}
                          </td>
                        </tr>
                      )}
                      {sortedMembers.map((member) => (
                        <MemberRow
                          key={member.id}
                          member={member}
                          isCurrentUser={member.id === currentUserId}
                          showMemberMenu={showMemberMenu && !member.isPending}
                          onMemberAction={onMemberAction}
                          showInviteMenu={showInviteMenu && member.isPending}
                          onInviteAction={onInviteAction}
                        />
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const SKELETON_ROW_COUNT = 6
const SKELETON_NAME_WIDTHS = ['62%', '45%', '71%', '52%', '66%', '55%']

function MemberRowsSkeleton({ hasActionsColumn }: { hasActionsColumn: boolean }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
        <tr key={i} className="border-b border-brand-neutral-200 last:border-b-0" aria-hidden>
          <td className="px-3 py-4 sm:px-5">
            <div className="flex items-center gap-3">
              <div className="size-8 shrink-0 rounded-full bg-brand-neutral-200" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <LoadingSkeleton width={SKELETON_NAME_WIDTHS[i]} lineHeight="0.875rem" />
                <LoadingSkeleton width="45%" lineHeight="0.75rem" />
              </div>
            </div>
          </td>
          <td className="px-5 py-4">
            <LoadingSkeleton width="50%" lineHeight="0.875rem" />
          </td>
          {hasActionsColumn && <td className="w-10 py-4" />}
        </tr>
      ))}
    </>
  )
}

function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return null
  const Icon = dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <div className="inline-flex items-center justify-center shrink-0 size-6">
      <div className="bg-brand-neutral-200 rounded-full p-1 flex items-center justify-center shrink-0">
        <Icon className="text-brand-neutral-600 size-3.5" aria-hidden />
      </div>
    </div>
  )
}

interface MemberRowProps {
  member: MemberRosterMember
  isCurrentUser: boolean
  showMemberMenu?: boolean
  onMemberAction: () => void
  showInviteMenu?: boolean
  onInviteAction: () => void
}

function MemberRow({ member, isCurrentUser, showMemberMenu, onMemberAction, showInviteMenu, onInviteAction }: MemberRowProps) {
  const initials = getInitialsFromString(member.displayName ?? member.email)
  const nameText = member.displayName ?? member.email
  const tooltipLabel = isCurrentUser ? `${nameText} (you)` : nameText

  // Reveal the full name/email via a tap-friendly tooltip when the cell clips them (common on small screens,
  // where the native `title` attribute never surfaces on touch).
  const { ref: nameRef, isTruncated: isNameTruncated } = useIsTruncated<HTMLParagraphElement>()
  const { ref: emailRef, isTruncated: isEmailTruncated } = useIsTruncated<HTMLParagraphElement>()

  return (
    <tr className="border-b border-brand-neutral-200 last:border-b-0">
      <td className="px-3 py-4 sm:px-5">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              'relative flex size-8 shrink-0 items-center justify-center rounded-full font-medium font-rethink-sans text-sm',
              member.isPending ? 'text-true-gray-400' : 'bg-brand-neutral-200 text-brand-neutral-700'
            )}
          >
            {member.isPending && <DashedAvatarRing className="absolute inset-0 size-full" aria-hidden />}
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <Tooltip title={isNameTruncated ? tooltipLabel : ''} placement="top">
              <span className="flex min-w-0 items-center gap-1.5">
                <p ref={nameRef} className="min-w-0 truncate text-sm font-medium text-brand-neutral-950">
                  {nameText}
                  {isCurrentUser && <span className="text-brand-neutral-500"> (you)</span>}
                </p>
                {member.isPending && (
                  <span className="hidden flex-none items-center sm:flex">
                    <Tag variant="tonal" color="Black">
                      Pending
                    </Tag>
                  </span>
                )}
              </span>
            </Tooltip>
            <Tooltip title={isEmailTruncated ? member.email : ''} placement="top">
              <span className="flex min-w-0">
                <p ref={emailRef} className="min-w-0 flex-1 truncate text-xs text-brand-neutral-800">
                  {member.email}
                </p>
              </span>
            </Tooltip>
          </div>
        </div>
      </td>
      <td className="px-5 py-4 text-sm">
        {member.isPending ? (
          <span className="text-brand-neutral-500">—</span>
        ) : (
          <span className="text-brand-neutral-950">{member.role === UserRole.ADMIN ? 'Admin' : member.role === UserRole.USER ? 'Member' : '—'}</span>
        )}
      </td>
      {(showMemberMenu || showInviteMenu) && (
        <td className="w-10 py-4">
          <div className="flex justify-center">
            {member.isPending
              ? showInviteMenu && <InviteListItemMenu inviteId={member.id} inviteeEmail={member.email} refreshData={onInviteAction} stacked />
              : showMemberMenu &&
                member.userId && (
                  <UserListItemMenu
                    userId={member.userId}
                    userFirebaseId={member.id}
                    userEmail={member.email}
                    userRole={member.role}
                    isOrgPayer={member.isOrgPayer ?? false}
                    refreshData={onMemberAction}
                    stacked
                  />
                )}
          </div>
        </td>
      )}
    </tr>
  )
}
